import { useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Settings, Download } from "lucide-react";
import {
	Table as UITable,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
	TableCell,
	Skeleton,
	Button,
} from "@hotelise/ui";

import { ExpenseTableItem } from "components/molecules";
import { ColumnSettingsModal } from "components/organisms";
import { useColumnPreferences } from "hooks/useColumnPreferences";

import { Expense } from "interfaces/expenses";

const DEFAULT_COLUMNS = [
	{ id: "name", label: "Name", isVisible: true, width: "1fr" },
	{ id: "supplier", label: "Supplier", isVisible: true, width: "0.8fr" },
	{ id: "property", label: "Property", isVisible: true, width: "1fr" },
	{ id: "description", label: "Description", isVisible: true, width: "1.3fr" },
	{ id: "amount", label: "Amount", isVisible: true, width: "0.9fr" },
	{ id: "date", label: "Date", isVisible: true, width: "0.8fr" },
	{ id: "receipt", label: "Attachments", isVisible: true, width: "0.5fr" },
];

const SKEL_ROWS = ["a", "b", "c", "d", "e", "f", "g", "h"];
const SKEL_MOBILE_CARDS = ["a", "b", "c", "d", "e", "f"];

interface Props {
	expenses: Expense[];
	isLoading: boolean;
	hasMore: boolean;
	loadMore: () => void;
	showProperty?: boolean;
	scrollableTarget?: string;
	onExport?: () => void;
}

const ExpensesList = (props: Props): JSX.Element => {
	const { expenses, isLoading, hasMore, loadMore, showProperty, scrollableTarget, onExport } = props;
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	const { columns, toggleColumn } = useColumnPreferences("expensesList", DEFAULT_COLUMNS);

	const visibleColumns = useMemo(() => {
		return columns.filter((col) => {
			if (!showProperty && col.id === "property") return false;
			if (!col.isVisible) return false;
			return true;
		});
	}, [columns, showProperty]);

	const visibleColumnIds = useMemo(() => visibleColumns.map((c) => c.id), [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	/* ── Desktop skeleton ──────────────────────────────────── */
	const renderDesktopSkeleton = () => (
		<div className="rounded-md border overflow-hidden">
			<UITable>
				<TableHeader>
					<TableRow>
						{visibleColumns.map((col) => (
							<TableHead key={col.id}>
								<Skeleton className="h-4 w-full" />
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{SKEL_ROWS.map((key) => (
						<TableRow key={key}>
							{visibleColumns.map((col) => (
								<TableCell key={col.id}>
									<Skeleton className="h-4 w-3/4" />
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</UITable>
		</div>
	);

	/* ── Mobile skeleton ───────────────────────────────────── */
	const renderMobileSkeleton = () => (
		<div className="space-y-2">
			{SKEL_MOBILE_CARDS.map((key) => (
				<div key={key} className="rounded-lg border bg-card p-3 shadow-sm space-y-1.5">
					{/* Name + Amount */}
					<div className="flex items-center justify-between">
						<Skeleton className="h-4 w-32" />
						<Skeleton className="h-4 w-16" />
					</div>
					{/* Property + Date */}
					<div className="flex items-center justify-between">
						<Skeleton className="h-3 w-24" />
						<Skeleton className="h-3 w-20" />
					</div>
					{/* Supplier */}
					<div className="flex items-center justify-between">
						<Skeleton className="h-3 w-14" />
						<Skeleton className="h-3 w-20" />
					</div>
					{/* Description */}
					<Skeleton className="h-3 w-full" />
					{/* Attachments */}
					<div className="flex items-center gap-1 pt-0.5">
						<Skeleton className="h-7 w-7 rounded" />
						<Skeleton className="h-7 w-7 rounded" />
					</div>
				</div>
			))}
		</div>
	);

	/* ── Desktop load-more ─────────────────────────────────── */
	const renderDesktopLoadMore = () => (
		<div className="py-2">
			<UITable>
				<TableBody>
					{["lm-1", "lm-2", "lm-3"].map((rowId) => (
						<TableRow key={rowId}>
							{visibleColumns.map((col) => (
								<TableCell key={`${rowId}-${col.id}`}>
									<Skeleton className="h-4 w-3/4" />
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</UITable>
		</div>
	);

	/* ── Mobile load-more ──────────────────────────────────── */
	const renderMobileLoadMore = () => (
		<div className="py-2 space-y-2">
			{["lm-1", "lm-2"].map((id) => (
				<div key={id} className="rounded-lg border bg-card p-3 shadow-sm space-y-1.5">
					<div className="flex items-center justify-between">
						<Skeleton className="h-4 w-32" />
						<Skeleton className="h-4 w-16" />
					</div>
					<div className="flex items-center justify-between">
						<Skeleton className="h-3 w-24" />
						<Skeleton className="h-3 w-20" />
					</div>
					<div className="flex items-center justify-between">
						<Skeleton className="h-3 w-14" />
						<Skeleton className="h-3 w-20" />
					</div>
					<Skeleton className="h-3 w-full" />
				</div>
			))}
		</div>
	);

	/* ── Content (desktop + mobile) ───────────────────────── */
	const renderContent = () => {
		if (isLoading && expenses.length === 0) {
			return (
				<>
					<div className="hidden md:block">
						{renderDesktopSkeleton()}
					</div>
					<div className="md:hidden">
						{renderMobileSkeleton()}
					</div>
				</>
			);
		}

		if (expenses.length === 0) {
			return (
				<>
					<div className="hidden md:block">
						<div className="rounded-md border overflow-hidden">
							<UITable>
								<TableHeader>
									<TableRow>
										{visibleColumns.map((col) => (
											<TableHead key={col.id}>{col.label}</TableHead>
										))}
									</TableRow>
								</TableHeader>
								<TableBody>
									<TableRow>
										<TableCell
											colSpan={visibleColumns.length}
											className="text-center text-sm text-muted-foreground py-8"
										>
											No expenses found.
										</TableCell>
									</TableRow>
								</TableBody>
							</UITable>
						</div>
					</div>
					<div className="md:hidden">
						<p className="text-center text-sm text-muted-foreground py-8">
							No expenses found.
						</p>
					</div>
				</>
			);
		}

		return (
			<InfiniteScroll
				scrollableTarget={scrollableTarget}
				dataLength={expenses.length}
				next={loadMore}
				hasMore={hasMore}
				loader={
					<>
						<div className="hidden md:block">{renderDesktopLoadMore()}</div>
						<div className="md:hidden">{renderMobileLoadMore()}</div>
					</>
				}
				className="!overflow-hidden relative"
			>
				{/* Desktop */}
				<div className="hidden md:block">
					<div className="rounded-md border overflow-hidden">
						<UITable>
							<TableHeader>
								<TableRow>
									{isColumnVisible("name") && <TableHead>Name</TableHead>}
									{isColumnVisible("supplier") && <TableHead>Supplier</TableHead>}
									{showProperty && isColumnVisible("property") && <TableHead>Property</TableHead>}
									{isColumnVisible("description") && <TableHead>Description</TableHead>}
									{isColumnVisible("amount") && <TableHead>Amount</TableHead>}
									{isColumnVisible("date") && <TableHead>Date</TableHead>}
									{isColumnVisible("receipt") && <TableHead>Attachments</TableHead>}
								</TableRow>
							</TableHeader>
							<TableBody>
								{expenses.map((expense: Expense) => (
									<ExpenseTableItem
										showProperty={showProperty}
										data={expense}
										key={expense.id}
										visibleColumnIds={visibleColumnIds}
									/>
								))}
							</TableBody>
						</UITable>
					</div>
				</div>

				{/* Mobile */}
				<div className="md:hidden space-y-2">
					{expenses.map((expense: Expense) => (
						<ExpenseTableItem
							showProperty={showProperty}
							data={expense}
							key={expense.id}
							visibleColumnIds={visibleColumnIds}
							isMobile
						/>
					))}
				</div>

			</InfiniteScroll>
		);
	};

	return (
		<div className="py-4 overflow-y-auto pb-24 md:pb-10">
			<div className="flex items-center justify-end gap-2 mb-3 md:mb-2.5">
				{onExport && (
					<>
						<Button
							variant="outline"
							size="sm"
							className="hidden md:flex gap-1.5"
							onClick={onExport}
						>
							<Download className="h-3.5 w-3.5" />
							Export CSV
						</Button>
						<Button
							variant="outline"
							size="sm"
							className="flex md:hidden gap-1"
							onClick={onExport}
							aria-label="Export CSV"
						>
							<Download className="h-3.5 w-3.5" />
						</Button>
					</>
				)}
				<button
					onClick={() => setIsSettingsModalOpen(true)}
					className="border-none bg-transparent cursor-pointer flex items-center justify-center hover:opacity-80"
				>
					<Settings className="h-5 w-5 text-foreground" />
				</button>
			</div>
			{renderContent()}
			<ColumnSettingsModal
				isVisible={isSettingsModalOpen}
				close={() => setIsSettingsModalOpen(false)}
				columns={columns.filter((c) => showProperty || c.id !== "property")}
				onToggle={toggleColumn}
			/>
		</div>
	);
};

export default ExpensesList;
